import { addDays } from "date-fns";
import React, { useMemo } from "react";
import { useApi } from "../../../containers/ApiContainer";
import { useCreateTrip } from "../../../hooks/useCreateTrip";
import { TripService } from "../../../services/TripService";
import { Aircraft, Tenant, TenantType, Trip, TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { CreateTripDialogPresenter } from "./CreateTripDialogPresenter";

interface CreateTripDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (trip: Trip) => void;
  /**
   * Optional initial arrival date to pre-populate the form.
   * Useful when opening from scheduled view to match the current viewing time.
   */
  initialArrivalDate?: Date;
  /**
   * Optional initial departure date to pre-populate the form.
   * If not provided, defaults to 2 days after arrival date.
   */
  initialDepartureDate?: Date;
  /**
   * Optional initial location to assign the trip to.
   * If provided, the trip will be created and immediately assigned to this location.
   */
  currentLocationId?: string;
  /**
   * Type of the current location ('hangar' or 'ramp')
   */
  currentLocationType?: "hangar" | "ramp";
  /**
   * Enable simple mode for adding trips to existing tenants.
   * When true, hides tenant selection and uses existingTenant data.
   */
  simpleMode?: boolean;
  /**
   * Existing trip/tenant information for simple mode.
   * Used to pre-populate and skip tenant creation.
   */
  existingTenant?: TripWithTenant;
}

export const CreateTripDialog: React.FC<CreateTripDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialArrivalDate,
  initialDepartureDate,
  currentLocationId,
  currentLocationType,
  simpleMode = false,
  existingTenant,
}) => {
  // Initialize shared hook with initial form data
  const initialFormData = React.useMemo(
    () => ({
      arrivalDate: initialArrivalDate || new Date(),
      departureDate:
        initialDepartureDate || addDays(initialArrivalDate || new Date(), 2),
    }),
    [initialArrivalDate, initialDepartureDate]
  );

  const {
    formData,
    setFormData,
    loading,
    error,
    validationErrors,
    createTrip,
    setError,
    isFormValid,
    tenants,
  } = useCreateTrip(initialFormData);

  // Additional UI state
  const [conflictWarning, setConflictWarning] = React.useState<string | null>(
    null
  );
  const [checkingConflicts, setCheckingConflicts] = React.useState(false);

  // Initialize services for conflict checking
  const { postgrest } = useApi();
  const tripService = useMemo(() => new TripService(postgrest), [postgrest]);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      // Calculate dates with proper validation
      const arrivalDate = initialArrivalDate || new Date();
      let departureDate = initialDepartureDate || addDays(arrivalDate, 2);

      // Ensure departure is after arrival
      if (departureDate <= arrivalDate) {
        departureDate = addDays(arrivalDate, 2);
      }

      if (simpleMode && existingTenant) {
        // In simple mode, pre-populate with existing tenant data
        setFormData({
          tailNumber: existingTenant.tail_number || "",
          ownerManager: existingTenant.owner_manager || "",
          tenantType: TenantType.TRANSIENT,
          selectedAircraft: existingTenant.aircraft_model
            ? ({
                id: "",
                make: existingTenant.aircraft_manufacturer || "",
                model: existingTenant.aircraft_model || "",
              } as Aircraft)
            : null,
          selectedTenant: {
            id: existingTenant.tenant_id,
            tail_number: existingTenant.tail_number,
            owner_manager: existingTenant.owner_manager,
            aircraft: existingTenant.aircraft_model
              ? ({
                  id: "",
                  make: existingTenant.aircraft_manufacturer || "",
                  model: existingTenant.aircraft_model || "",
                } as Aircraft)
              : undefined,
          } as Tenant,
          arrivalDate,
          departureDate,
          notes: "",
          csrStatus: "requested",
        });
      } else {
        // Normal mode - empty form
        setFormData({
          tailNumber: "",
          ownerManager: "",
          tenantType: TenantType.TRANSIENT,
          selectedAircraft: null,
          selectedTenant: null,
          arrivalDate,
          departureDate,
          notes: "",
          csrStatus: "requested",
        });
      }
      setError(null);
      setConflictWarning(null);
    }
  }, [
    open,
    initialArrivalDate,
    initialDepartureDate,
    simpleMode,
    existingTenant,
    setFormData,
    setError,
  ]);

  // Check for conflicts with existing trips
  const checkForConflicts = React.useCallback(
    async (
      tenantId: string,
      arrival: Date,
      departure: Date | string
    ): Promise<string | null> => {
      if (!tenantId || !arrival || !departure) {
        return null;
      }

      try {
        const existingTrips = await tripService.listTripsByTenant(tenantId);

        // Check each existing trip for overlap
        for (const trip of existingTrips) {
          const existingArrival = new Date(trip.arrival_date);
          const existingDeparture = trip.departure_date; // Keep as string, might be infinity

          // Handle infinity dates in overlap detection
          const isCurrentInfinity = isInfinityDate(departure);
          const isExistingInfinity = isInfinityDate(existingDeparture);

          let overlaps = false;

          if (isCurrentInfinity && isExistingInfinity) {
            // Both infinity - only one open-ended trip allowed
            overlaps = true;
          } else if (isCurrentInfinity) {
            // New trip has infinity - overlaps if it starts before existing finite trip ends
            const existingDepartureDate = new Date(existingDeparture);
            overlaps = arrival < existingDepartureDate;
          } else if (isExistingInfinity) {
            // Existing trip has infinity (open-ended) - allow new finite trips
            // This supports multiple visits for the same tenant (e.g., base tenant with transient visits)
            overlaps = false;
          } else {
            // Both finite - use standard overlap logic
            // Two ranges [A1, A2] and [B1, B2] overlap if A1 < B2 AND A2 > B1
            const departureDate = new Date(departure as string);
            const existingDepartureDate = new Date(existingDeparture);

            overlaps = arrival < existingDepartureDate && departureDate > existingArrival;
          }

          if (overlaps) {
            const arrivalStr = existingArrival.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
            const departureStr = isExistingInfinity
              ? "No scheduled departure"
              : new Date(existingDeparture).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });

            return `This aircraft already has a trip scheduled from ${arrivalStr} to ${departureStr}. Please adjust your dates to avoid overlap.`;
          }
        }
      } catch (error) {
        console.error("Failed to check for conflicts:", error);
        // Don't block creation if we can't check - database will enforce
      }

      return null;
    },
    [tripService]
  );

  // Handle tenant selection from SelectTailNumber
  const handleTenantChange = (tenant: Tenant) => {
    if (tenant) {
      // Autofill form fields from selected tenant
      setFormData({
        selectedTenant: tenant,
        tailNumber: tenant.tail_number || "",
        ownerManager: tenant.owner_manager || "",
        tenantType: tenant.type || TenantType.TRANSIENT,
        selectedAircraft: tenant.aircraft || null,
      });
    } else {
      setFormData({ selectedTenant: null });
    }
  };

  // Handle tail number input change
  const handleTailNumberChange = (tailNumber: string) => {
    setFormData({ tailNumber });
    // Clear selected tenant if user is typing a new tail number
    if (
      formData.selectedTenant &&
      formData.selectedTenant.tail_number !== tailNumber
    ) {
      setFormData({ selectedTenant: null });
    }
    // Clear conflict warning when changing aircraft
    setConflictWarning(null);
  };

  // Check for conflicts when dates or tenant change
  React.useEffect(() => {
    if (
      formData.selectedTenant &&
      formData.arrivalDate &&
      formData.departureDate &&
      open
    ) {
      setCheckingConflicts(true);
      checkForConflicts(
        formData.selectedTenant.id,
        formData.arrivalDate,
        formData.departureDate
      )
        .then((conflict) => {
          setConflictWarning(conflict);
          setCheckingConflicts(false);
        })
        .catch(() => {
          setCheckingConflicts(false);
        });
    } else {
      setConflictWarning(null);
    }
  }, [
    formData.selectedTenant,
    formData.arrivalDate,
    formData.departureDate,
    open,
    checkForConflicts,
  ]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const trip = await createTrip({
        locationId: currentLocationId,
        locationType: currentLocationType,
        useGapFilling: false, // Use atomic createTripWithLocation for dialog
      });

      onSuccess(trip);
      onClose();
    } catch (err: any) {
      console.error("Failed to create trip:", err);
      // Error is already set by the shared hook
    }
  };

  return (
    <CreateTripDialogPresenter
      open={open}
      onClose={onClose}
      formData={formData}
      setFormData={setFormData}
      handleTenantChange={handleTenantChange}
      handleTailNumberChange={handleTailNumberChange}
      handleSubmit={handleSubmit}
      validationErrors={validationErrors}
      isFormValid={isFormValid}
      loading={loading}
      checkingConflicts={checkingConflicts}
      error={error}
      setError={setError}
      conflictWarning={conflictWarning}
      setConflictWarning={setConflictWarning}
      tenants={tenants}
      currentLocationId={currentLocationId}
      currentLocationType={currentLocationType}
      simpleMode={simpleMode}
      existingTenant={existingTenant}
    />
  );
};
